'use client';

import { OnMutateFn } from '@diritto/common/base/mutateFunction';
import { ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ReplyOrder } from '@diritto/common/reply/constants';
import { CreateReplyDto, UpdateReplyDto } from '@diritto/common/reply/dtos/create-reply.dto';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { RelayReplyApi } from 'api/ApiHelper';
import { ReplyEditAction } from 'constants/reply';
import { useTrackingState } from 'contexts/TrackingProvider';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCreateEpisodeReplyMutation } from 'queries/reply/mutations/useCreateEpisodeReplyMutation';
import { useDeleteReplyByAuthorMutation } from 'queries/reply/mutations/useDeleteReplyByAuthorMutation';
import { useDeleteReplyMutation } from 'queries/reply/mutations/useDeleteReplyMutation';
import { useUpdateReplyMutation } from 'queries/reply/mutations/useUpdateReplyMutation';
import { useInvalidateEpisodeReplyQueries } from 'queries/reply/useInvalidateEpisodeReplyQueries';
import { createContext, ReactNode, RefObject, useCallback, useContext, useMemo, useState } from 'react';
import { useReplyOrderType } from 'state-hooks/reply/useReplyOrderType';
import { LocalStorage } from 'utils/LocalStorage';

interface EpisodeReplyState {
  contentId: number;
  episodeId?: number;
  authorProfile?: ProfileLitePublicRelayDto;
  editReply?: ReplyRelayDto;
  isInputFocus: boolean;
}

type DispatchParams = {
  reply?: ReplyRelayDto;
  createReply?: CreateReplyDto;
  updateReply?: UpdateReplyDto;
  onMutate?: OnMutateFn;
};

interface EpisodeEditDispatch {
  isPending: boolean;
  onSettled: () => void;
  onChangeOrderBy: (order: ReplyOrder) => void;
  dispatchReplyAction: (action: ReplyEditAction, params: DispatchParams) => Promise<void>;
}

interface ReplyProviderProps {
  children: ReactNode;
  contentId: number;
  episodeId?: number;
  profileId?: number;
  parentReplyId?: number;
  orderBy?: ReplyOrder;
  authorProfile?: ProfileLitePublicRelayDto;
  isInputFocus?: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
}

const EpisodeReplyStateContext = createContext<EpisodeReplyState & { orderBy: ReplyOrder }>({
  authorProfile: undefined,
  contentId: 0,
  orderBy: ReplyOrder.Latest,
  isInputFocus: false,
});

const EpisodeReplyDispatchContext = createContext<EpisodeEditDispatch>({
  dispatchReplyAction(): Promise<void> {
    return Promise.resolve(undefined);
  },
  onSettled(): void {},
  isPending: false,
  onChangeOrderBy(): void {},
});

export const EpisodeReplyProvider = ({
  children,
  contentId,
  episodeId,
  profileId,
  parentReplyId,
  orderBy,
  authorProfile,
  isInputFocus = false,
  containerRef,
}: ReplyProviderProps) => {
  const [state, setState] = useState<EpisodeReplyState>({
    contentId,
    episodeId,
    authorProfile,
    isInputFocus,
  });
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const createQueryString = useCallback(
    (params: Record<string, string>) => {
      const newSearchParams = new URLSearchParams((searchParams ?? '').toString());
      Object.entries(params).forEach(([name, value]) => {
        newSearchParams.set(name, value);
      });

      return newSearchParams.toString();
    },
    [searchParams],
  );

  const { tracker } = useTrackingState();
  const refetchReplies = useInvalidateEpisodeReplyQueries({ contentId, episodeId, profileId, replyId: parentReplyId });
  const { orderType, setOrderType } = useReplyOrderType({ initialOrderType: orderBy });

  const onSettled = useCallback(() => {
    setState(prev => ({ ...prev, editReply: undefined, isInputFocus: false }));
    refetchReplies();
  }, [refetchReplies]);

  const trackingParams = useCallback(
    (replyId: number) => (episodeId ? { contentId, episodeId, replyId } : { contentId, replyId }),
    [contentId, episodeId],
  );

  const handleChangeOrderBy = useCallback(
    (order: ReplyOrder, isChangePath: boolean = true) => {
      LocalStorage.setItem(LocalStorageKey.EpisodeReplyOrder, order);
      setOrderType(order);

      containerRef?.current?.scrollTo(0, 0);
      if (isChangePath) {
        window.history.replaceState({}, '', `${pathname}?${createQueryString({ orderBy: order })}`);
      }
    },
    [containerRef, setOrderType, createQueryString, pathname],
  );

  const { mutate: createMutate, isPending: isPendingCreate } = useCreateEpisodeReplyMutation(contentId, episodeId!, {
    onSettled,
    onSuccess: async data => {
      tracker.track(TrackingEventType.CreateReply, trackingParams(data.reply.id));

      RelayReplyApi.getReply(data.reply.id).then(() => {
        handleChangeOrderBy(ReplyOrder.Latest, false);
        router.replace(
          `${pathname}?${createQueryString({ orderBy: ReplyOrder.Latest, focusedReplyId: data.reply.id.toString() })}`,
        );
      });
    },
  });

  const { mutate: updateMutate, isPending: isPendingUpdate } = useUpdateReplyMutation({
    onSettled,
    onSuccess: (data, variables) => tracker.track(TrackingEventType.UpdateReply, trackingParams(variables.replyId)),
  });

  const { mutate: deleteMutate, isPending: isPendingDelete } = useDeleteReplyMutation({
    onSettled,
    onSuccess: (_, replyId) => tracker.track(TrackingEventType.DeleteReply, trackingParams(replyId)),
  });

  const { mutate: deleteByAuthorMutate } = useDeleteReplyByAuthorMutation(contentId, { onSettled });

  const dispatchReplyAction = useCallback(
    async (action: ReplyEditAction, { reply, createReply, updateReply, onMutate }: DispatchParams) => {
      switch (action) {
        case ReplyEditAction.StartEdit:
          setState(prev => ({ ...prev, isInputFocus: true }));
          return;
        case ReplyEditAction.CancelEdit:
          setState(prev => ({ ...prev, editReply: undefined, isInputFocus: false }));
          return;
        case ReplyEditAction.ActiveEdit:
          setState(prev => ({ ...prev, editReply: reply, isInputFocus: true }));
          return;
        case ReplyEditAction.Create:
          setState(prev => ({ ...prev, isInputFocus: true }));
          if (!isPendingCreate && createReply) {
            await createMutate(createReply, onMutate);
          }
          return;
        case ReplyEditAction.Update:
          setState(prev => ({ ...prev, isInputFocus: true }));
          if (reply && updateReply) {
            await updateMutate({ replyId: reply.id, replyDto: updateReply }, onMutate);
          }
          return;
        case ReplyEditAction.DeleteByUser:
          if (reply) {
            await deleteMutate(reply.id, onMutate);
          }
          return;
        case ReplyEditAction.DeleteByAuthor:
          if (reply) {
            await deleteByAuthorMutate(reply.id, onMutate);
          }
          return;
        default:
          console.log("Can't find action");
      }
    },
    [createMutate, deleteByAuthorMutate, deleteMutate, isPendingCreate, updateMutate],
  );

  const dispatch = useMemo<EpisodeEditDispatch>(
    () => ({
      dispatchReplyAction,
      onSettled,
      onChangeOrderBy: handleChangeOrderBy,
      isPending: isPendingCreate || isPendingUpdate || isPendingDelete,
    }),
    [dispatchReplyAction, isPendingCreate, isPendingDelete, isPendingUpdate, handleChangeOrderBy, onSettled],
  );

  const value = useMemo(() => ({ ...state, orderBy: orderType }), [orderType, state]);

  return (
    <EpisodeReplyDispatchContext.Provider value={dispatch}>
      <EpisodeReplyStateContext.Provider value={value}>{children}</EpisodeReplyStateContext.Provider>
    </EpisodeReplyDispatchContext.Provider>
  );
};

export const useEpisodeReplyDispatch = () => useContext(EpisodeReplyDispatchContext);

export const useEpisodeReplyState = () => {
  const context = useContext(EpisodeReplyStateContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useEpisodeReplyState was used outside of its Provider');
  }
  return context;
};
